import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function Dashboard({ user }) {
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [operadorasPermitidas, setOperadorasPermitidas] = useState([]);

  useEffect(() => {
    loadLimiteInfo();
    loadOperadorasPermitidas();
  }, [user]);

  const loadLimiteInfo = async () => {
    try {
      setLoading(true);
      if (!user) {
        setLoading(false);
        return;
      }

      // Admin não tem limite
      if (user.role === 'admin') {
        setLoading(false);
        return;
      }

      let proprietarioId = user.id;
      if (user.role === 'usuario' && user.criador_id) {
        proprietarioId = user.criador_id;
      }

      const response = await axios.get(`/users/${proprietarioId}/limite-consultas-info`);
      setLimiteInfo(response.data);
    } catch (err) {
      console.error('Erro ao carregar informações de limite:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOperadorasPermitidas = async () => {
    try {
      if (!user) return;

      // Se for admin, tem acesso a todas as operadoras
      if (user.role === 'admin') {
        setOperadorasPermitidas(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
        return;
      }

      // Buscar operadoras permitidas do backend
      const response = await axios.get('/users/me');
      const currentUserData = response.data;
      
      if (currentUserData) {
        // Usar operadoras_permitidas_calculadas se disponível
        if (currentUserData.operadoras_permitidas_calculadas) {
          setOperadorasPermitidas(currentUserData.operadoras_permitidas_calculadas);
        } else {
          // Fallback: usar operadoras_permitidas direto do usuário
          let operadoras = [];
          if (currentUserData.operadoras_permitidas) {
            try {
              operadoras = typeof currentUserData.operadoras_permitidas === 'string'
                ? JSON.parse(currentUserData.operadoras_permitidas)
                : currentUserData.operadoras_permitidas;
              if (!Array.isArray(operadoras)) operadoras = [];
            } catch (e) {
              operadoras = [];
            }
          }
          setOperadorasPermitidas(operadoras);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar operadoras permitidas:', err);
      setOperadorasPermitidas([]);
    }
  };

  const getOperadorasNames = () => {
    const nomesOperadoras = {
      'vivo': 'VIVO',
      'claro': 'CLARO',
      'tim': 'TIM',
      'desktop': 'DESKTOP',
      'gigamais': 'GIGA MAIS',
      'vero': 'VERO',
      'nio': 'NIO',
      'brisanet': 'BRISANET',
      'aonet': 'AONET',
      'ligga': 'LIGGA',
      'mhnet': 'MHNET',
      'ampernet': 'AMPERNET',
      'zaaz': 'ZAAZ',
      'uno': 'UNO'
    };
    
    if (operadorasPermitidas.length === 0) {
      return 'Nenhuma operadora disponível';
    }
    
    return operadorasPermitidas.map(op => nomesOperadoras[op] || op.toUpperCase()).join(', ');
  };

  const getPorcentagem = () => {
    if (!limiteInfo || limiteInfo.limite === null) return 0;
    return (limiteInfo.usadas / limiteInfo.limite) * 100;
  };

  const getCorBarra = () => {
    if (!limiteInfo || limiteInfo.limite === null) return '#00cc66';
    const porcentagem = getPorcentagem();
    if (porcentagem >= 100) return '#ff0000';
    if (porcentagem >= 90) return '#ff6600';
    return '#00cc66';
  };

  return (
    <div>
      <nav className="nav">
        <Link to="/dashboard" className="nav-link active">
          Dashboard
        </Link>
        <Link to="/consultas" className="nav-link">
          Consultas
        </Link>
        <Link to="/consulta-cpf" className="nav-link">
          Consulta CPF
        </Link>
        <Link to="/consulta-cpf-tem-cnpj" className="nav-link">
          Consulta CPF TEM CNPJ
        </Link>
        {(user?.role === 'admin' || user?.role === 'proprietario') && (
          <Link to="/admin" className="nav-link">
            Administração
          </Link>
        )}
        {(user?.role === 'admin' || user?.role === 'proprietario') && (
          <Link to="/limites-consultas" className="nav-link">
            Limites
          </Link>
        )}
      </nav>

      <div className="card">
        <h2>Bem-vindo, {user?.username}!</h2>
        <p style={{ color: '#999', marginBottom: '2rem' }}>
          Selecione uma opção abaixo para realizar consultas nas APIs.
        </p>

        <div className="consultas-grid">
          <Link to="/consultas" style={{ textDecoration: 'none' }}>
            <div className="consulta-card">
              <h3>Consultas</h3>
              <p>Acesse as consultas de {getOperadorasNames()}</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Card de Limite de Consultas */}
      {!loading && limiteInfo && limiteInfo.limite !== null && (
        <div className="card">
          <h2>Consultas Restantes Este Mês</h2>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#999' }}>Consultas usadas:</span>
              <strong style={{ color: getCorBarra() }}>
                {limiteInfo.usadas || 0} / {limiteInfo.limite || 0}
              </strong>
            </div>
            <div style={{ 
              width: '100%', 
              height: '30px', 
              backgroundColor: '#333', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(getPorcentagem(), 100)}%`,
                height: '100%',
                backgroundColor: getCorBarra(),
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
            <p style={{ margin: '0.5rem 0', color: '#999' }}>
              <strong style={{ color: '#cc0000' }}>Restantes:</strong>{' '}
              <span style={{ color: getCorBarra(), fontWeight: 'bold', fontSize: '1.2rem' }}>
                {limiteInfo.restantes !== null ? limiteInfo.restantes : 'Ilimitado'}
              </span>
            </p>
            {limiteInfo.tipo && (
              <p style={{ margin: '0.5rem 0', color: '#999', fontSize: '0.9rem' }}>
                <strong>Tipo:</strong> {limiteInfo.tipo === 'individual' ? 'Limite Individual' : 'Pool Compartilhado'}
              </p>
            )}
          </div>

          {limiteInfo.restantes === 0 && (
            <div className="alert alert-error" style={{ marginTop: '1rem' }}>
              <strong>⚠️ Limite Atingido</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                Você atingiu o limite de consultas para este mês. O limite será resetado no dia 1 do próximo mês.
              </p>
            </div>
          )}

          {(user?.role === 'admin' || user?.role === 'proprietario') && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
              <Link to="/limites-consultas" style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none', width: '100%' }} className="btn">
                Gerenciar Limites
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2>Informações da Conta</h2>
        <table>
          <tbody>
            <tr>
              <td><strong>Usuário:</strong></td>
              <td>{user?.username}</td>
            </tr>
            <tr>
              <td><strong>Role:</strong></td>
              <td>{user?.role}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Dashboard;
